import { forwardRef, useCallback, useId, useImperativeHandle, useMemo } from "react";
import { createFormHook } from "@tanstack/react-form";
import { isObject } from "lodash";
import type { FormAPI, FormAdapterProps } from "../types";
import { FieldRefsContext, useFieldRefsAPI } from "./FieldRefsContext";
import { FormAPIContext, fieldContext, formContext } from "./contexts";
import { ValidatorProvider } from "./validator";

export const { useAppForm } = createFormHook({
    fieldComponents: {},
    formComponents: {},
    fieldContext,
    formContext,
});

export const FormAdapter = forwardRef<FormAPI, FormAdapterProps>((props, ref) => {
    const { defaultValues, onSubmit, children, className, ...dataAttrs } = props;

    const formId = useId();

    const form = useAppForm({
        defaultValues,
        onSubmit: async ({ value, meta }) => {
            await onSubmit(
                value,
                // TS struggles to infer generic types in components wrapped in forwardRef
                // So we resort to type assertion here
                meta as Parameters<typeof onSubmit>[1],
            );
        },
    });

    const fieldRefsContextValue = useFieldRefsAPI();

    const setFocus = useCallback(
        (path: string) => {
            const element = fieldRefsContextValue.getFieldRef(path);

            if (isObject(element) && "focus" in element && typeof element.focus === "function") {
                element.focus();
            }
        },
        [fieldRefsContextValue], // dependencies are guaranteed to never change
    );

    const formAPI = useMemo<FormAPI>(
        () => ({
            reset: form.reset,

            getFieldValue: (path) => form.getFieldValue(path) as any,
            getValues: () => form.state.values,
            setFieldValue: (path, value) => form.setFieldValue(path, value),
            setValues: (values) => {
                // TanStack Form does not expose a bulk setValues/patchValue API.
                // Iterating over setFieldValue is the de facto supported workaround.
                for (const [key, value] of Object.entries(values)) {
                    form.setFieldValue(key, value);
                }
            },
            appendArrayItem: (path, value) => {
                const currentArray = form.getFieldValue(path) as unknown[];
                const index = Array.isArray(currentArray) ? currentArray.length : 0;
                form.insertFieldValue(path, index, value);
            },
            removeArrayItem: (path, index) => form.removeFieldValue(path, index),
            handleSubmit: form.handleSubmit,
            setFocus,
        }),
        [form, setFocus], // dependencies are guaranteed to never change
    );

    useImperativeHandle(ref, () => formAPI);

    return (
        <form
            id={formId}
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                form.handleSubmit();
            }}
            className={className}
            {...dataAttrs}
        >
            <form.AppForm>
                <FormAPIContext.Provider value={formAPI}>
                    <FieldRefsContext.Provider value={fieldRefsContextValue}>
                        <ValidatorProvider formId={formId}>{children}</ValidatorProvider>
                    </FieldRefsContext.Provider>
                </FormAPIContext.Provider>
            </form.AppForm>
        </form>
    );
});

FormAdapter.displayName = "FormAdapter";
